/**
 * Popup shown when tapping a firefly (replay or live cursor).
 * Composes ModalFrame with centred alignment and firefly-specific content.
 */
import { useTranslation } from 'react-i18next';
import ModalFrame from '../primitives/ModalFrame';
import UserAvatar from '../../components/UserAvatar';
import { formatUserName } from '../../utils/format/formatUserName';
import type { CursorUserInfo } from '../../client';


interface FireflyInfoModalProps {
  info:       CursorUserInfo | null;
  isLoading:  boolean;
  recordedAt: number | null;
  isAdmin?:   boolean;
  onClose:    () => void;
}

export default function FireflyInfoModal({
  info,
  isLoading,
  recordedAt,
  isAdmin = false,
  onClose,
}: FireflyInfoModalProps) {
  const { t } = useTranslation();

  const { shortName, usernameFormatted } = info
    ? formatUserName(info)
    : { shortName: '', usernameFormatted: null };

  return (
    <ModalFrame
      isOpen={isLoading || info !== null}
      onClose={onClose}
      title={t("Player's trail")}
      variant="dialog"
      className="max-w-[280px] rounded-xl"
      headerClassName="text-[15px] font-semibold"
      showBackButton
    >
      {isLoading ? (
        <div className="py-md text-center text-[14px] text-white-alpha-60">
          …
        </div>
      ) : info ? (
        <div className="flex flex-col items-center gap-lg">
          <UserAvatar
            user={info}
            size={72}
          />

          {/* Firefly trace badge */}
          <div className="flex items-center gap-sm text-[12px] text-white-alpha-55">
            <span className="inline-block h-[10px] w-[10px] rounded-full bg-firefly-info-modal-surface shadow-[0_0_6px_3px_var(--color-firefly-info-modal-shadow)]" />
            {t('Firefly trace')}
          </div>

          <div className="text-center text-[17px] font-bold text-white">
            {shortName}
          </div>

          {usernameFormatted && (
            <div className="text-[13px] text-white-alpha-45">
              {usernameFormatted}
            </div>
          )}

          <div className="rounded-[10px] border border-brand-alpha-30 bg-brand-alpha-15 px-xl py-sm text-[14px] font-bold text-brand">
            {t('Level')} {info.level}
          </div>

          {isAdmin && recordedAt && (
            <div className="text-[10px] font-bold text-duel-warning">
              {new Date(recordedAt * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
      ) : null}
    </ModalFrame>
  );
}
